import { Controller } from "@hotwired/stimulus"
import { defaultAssistantName } from "../utils/brand"

// The bridge between Jane's chat pane (assistants#panel) and the Chrome
// extension's side panel that frames it (extension/lib/jane-frame.js). The pane
// itself is the app's own - this only does what being inside a frame needs:
//
//   - says "ready" (or "signed-out") to the extension, which until then keeps
//     its own chat, so a frame that never answers costs nothing;
//   - takes the extension's questions and the page it is beside, and sends them
//     through the ordinary composer, the page as a chip beside the field;
//   - keeps the pane in place: a send follows the thread here rather than
//     moving the frame to /messages, and a link opens in a tab of its own.
//
// Inert anywhere but inside Jane's extension: the parent's origin must be one
// of the extension ids the server trusts (PersonalAccessToken.jane_extension_ids),
// and every message in is checked against it.
export default class extends Controller {
    // fit: a card (JanePanelFrame) rather than the chat - the frame is sized
    // to it, and its own forms update it in place as they do in the sidebar.
    static values = { conversationId: Number, origins: Array, url: String, signedOut: Boolean, fit: Boolean }

    connect() {
        this.parentOrigin = this._parentOrigin()
        if (!this.parentOrigin) return

        this._onMessage = (event) => this._receive(event)
        this._onSent = (event) => this._sent(event.detail?.conversationId)
        this._onClick = (event) => this._click(event)
        this._onSubmit = (event) => this._submit(event)
        window.addEventListener("message", this._onMessage)
        this.element.addEventListener("jane-sidebar:sent", this._onSent)
        this.element.addEventListener("click", this._onClick, true)
        this.element.addEventListener("submit", this._onSubmit, true)

        this._post(this.signedOutValue ? { type: "signed-out" } : { type: "ready", conversationId: this.conversationIdValue || null })

        // The card's height, whenever it changes (a section folds, a timeline
        // grows), so the panel can size the frame and scroll it with the page.
        // A card's overlays (a dialog, the email preview, the meeting note)
        // open in the slice of it the panel is showing - see _setViewport.
        if (this.fitValue) document.documentElement.classList.add("jane-panel-card-frame")

        if (this.fitValue && window.ResizeObserver) {
            this._lastHeight = 0
            this._resize = new ResizeObserver(() => {
                const height = Math.ceil(this.element.getBoundingClientRect().height)
                if (height === this._lastHeight) return
                this._lastHeight = height
                this._post({ type: "height", height })
            })
            this._resize.observe(this.element)
        }
    }

    disconnect() {
        if (!this.parentOrigin) return
        window.removeEventListener("message", this._onMessage)
        this.element.removeEventListener("jane-sidebar:sent", this._onSent)
        this.element.removeEventListener("click", this._onClick, true)
        this.element.removeEventListener("submit", this._onSubmit, true)
        this._resize?.disconnect()
    }

    // Who framed us. location.ancestorOrigins is the one place a frame can read
    // its parent's origin; an untrusted (or absent) parent leaves this inert.
    _parentOrigin() {
        if (window.parent === window) return null
        const origin = window.location.ancestorOrigins?.[0] || null
        return origin && this.originsValue.includes(origin) ? origin : null
    }

    _post(message) {
        window.parent.postMessage({ source: "jane-panel", ...message }, this.parentOrigin)
    }

    _receive(event) {
        if (event.origin !== this.parentOrigin || event.source !== window.parent) return
        const data = event.data
        if (!data || data.source !== "jane-extension") return
        if (data.type === "context") this._setContext(data.context)
        if (data.type === "viewport") this._setViewport(data)
        if (data.type === "browser") this._setBrowser(data.sessionId)
        if (data.type === "ask") {
            if (data.browserSessionId) this._setBrowser(data.browserSessionId)
            data.fresh ? this._askFresh(data.text, data.context) : this._ask(data.text, data.context)
        }
    }

    get form() {
        return this.element.querySelector("form.assistant-compose")
    }

    // The page as a chip on the composer, and as the browser_context the
    // message carries (Assistant::BrowserContext). Null takes it off.
    _setContext(context) {
        const form = this.form
        if (!form) return
        let input = form.querySelector("input[name='browser_context']")
        form.querySelector(".composer-page-chip")?.remove()
        const usable = context && typeof context === "object" && (context.title || context.url)
        if (!usable) {
            input?.remove()
            return
        }
        if (!input) {
            input = document.createElement("input")
            input.type = "hidden"
            input.name = "browser_context"
            form.prepend(input)
        }
        input.value = JSON.stringify(context)

        const chip = document.createElement("span")
        chip.className = "composer-page-chip"
        chip.title = `${defaultAssistantName()} sees this page with your message: ${context.title || context.url}`
        const title = document.createElement("span")
        title.className = "composer-page-chip-title"
        title.textContent = context.title || context.url
        const remove = document.createElement("button")
        remove.type = "button"
        remove.setAttribute("aria-label", "Leave this page out")
        remove.textContent = "×"
        remove.addEventListener("click", () => this._setContext(null))
        chip.append(title, remove)
        form.querySelector(".composer-context")?.prepend(chip)
    }

    // Which of the person's browsers is asking (BrowserSession.for), carried
    // by every message sent from here - typed or asked by the panel.
    _setBrowser(sessionId) {
        const form = this.form
        const id = Number(sessionId)
        if (!form || !Number.isInteger(id) || id <= 0) return
        this.browserSessionId = id
        let input = form.querySelector("input[name='browser_session_id']")
        if (!input) {
            input = document.createElement("input")
            input.type = "hidden"
            input.name = "browser_session_id"
            form.prepend(input)
        }
        input.value = String(id)
    }

    _ask(text, context) {
        const form = this.form
        const field = form?.querySelector(".assistant-compose-field")
        if (!form || !field || !String(text || "").trim()) return
        this._setContext(context)
        field.value = String(text)
        field.dispatchEvent(new Event("input", { bubbles: true }))
        form.requestSubmit()
    }

    // A new conversation for this question (asked beside a page with nothing
    // on it): start one, send the question into it, then open it here.
    async _askFresh(text, context) {
        if (!String(text || "").trim()) return
        const headers = { "X-CSRF-Token": this._csrf(), Accept: "application/json" }
        try {
            const started = new FormData()
            started.append("purpose", "chat")
            const fresh = await fetch("/assistant/new_conversation", { method: "POST", headers, body: started, credentials: "same-origin" }).then((r) => (r.ok ? r.json() : null))
            if (!fresh?.conversation_id) return this._ask(text, context)
            const message = new FormData()
            message.append("message", String(text))
            message.append("conversation_id", fresh.conversation_id)
            if (context && typeof context === "object") message.append("browser_context", JSON.stringify(context))
            if (this.browserSessionId) message.append("browser_session_id", String(this.browserSessionId))
            await fetch("/assistant/message", { method: "POST", headers, body: message, credentials: "same-origin" })
            this._post({ type: "sent", conversationId: fresh.conversation_id })
            window.location.replace(`${this.urlValue}?conversation_id=${encodeURIComponent(fresh.conversation_id)}`)
        } catch (_error) {
            this._ask(text, context)
        }
    }

    // The composer sent it. The first message of a new thread reloads into that
    // thread (the pane resolves its conversation on the way in); after that the
    // live wrapper is told to pick the reply up.
    _sent(conversationId) {
        // The extension starts checking for Jane's browser commands soon:
        // reading the page is the likeliest thing she does next.
        this._post({ type: "sent", conversationId: conversationId || null })
        if (conversationId && conversationId !== this.conversationIdValue) {
            window.location.replace(`${this.urlValue}?conversation_id=${encodeURIComponent(conversationId)}`)
            return
        }
        const panel = this.element.querySelector("[data-controller~='assistant-poll']")
        const poll = panel && this.application.getControllerForElementAndIdentifier(panel, "assistant-poll")
        poll?.restart()
    }

    _click(event) {
        // "New conversation" belongs to the sidebar's controller elsewhere;
        // here it starts one and reloads into it.
        const fresh = event.target.closest("[data-action*='jane-sidebar#newConversation']")
        if (fresh) {
            event.preventDefault()
            this._newConversation()
            return
        }
        // A card's Message opens their conversation in the app's sidebar,
        // which a frame does not have: nothing happened (Pieter, 2026-10-16).
        // In a tab of its own instead - their last conversation, or their card
        // with ?compose=1, which is what the panel's own card always did.
        const message = this.fitValue && event.target.closest("[data-controller~='contact-message']")
        if (message) {
            event.preventDefault()
            event.stopPropagation()
            const url = this._messageUrl(message)
            if (url) this._post({ type: "open", url })
            return
        }
        const link = event.target.closest("a[href]")
        if (!link || link.getAttribute("href").startsWith("#")) return
        // A link that acts in place (a Turbo method or stream - "Mark as work",
        // "Remove") is the card's own business, not a page to open. "_top" is
        // not in place: inside a frame it would load the whole app here.
        if (link.dataset.turboMethod || link.dataset.turboStream !== undefined || (link.dataset.turboFrame && link.dataset.turboFrame !== "_top")) return
        // Another of this pane's own threads (the menu's recent ones) opens here.
        if (this.urlValue && new URL(link.href, location.href).pathname === new URL(this.urlValue, location.href).pathname) return
        event.preventDefault()
        this._post({ type: "open", url: link.href })
    }

    // The frame is as tall as the card, so a fixed overlay filled - and
    // centred in - the whole card, mostly scrolled out of the panel's view
    // (the edit dialog's Save sat under the composer). The panel says which
    // slice is on screen; the card-frame CSS opens overlays there.
    _setViewport({ top, height }) {
        const t = Number(top)
        const h = Number(height)
        if (!Number.isFinite(t) || !Number.isFinite(h) || h <= 0) return
        const root = document.documentElement.style
        root.setProperty("--panel-view-top", `${Math.max(0, Math.round(t))}px`)
        root.setProperty("--panel-view-height", `${Math.round(h)}px`)
    }

    // Their card in Jane with ?compose=1, which opens their last conversation
    // in the app's own sidebar (contact_message_controller's autostart). The
    // conversation URL itself is that sidebar's fragment (/emails/:id/
    // detail_panel): opened as a tab it was a page with no page around it.
    _messageUrl(button) {
        const compose = button.dataset.contactMessageComposeUrlValue
        const contactId = compose && new URL(compose, location.href).searchParams.get("contact_id")
        return contactId ? new URL(`/contacts/${encodeURIComponent(contactId)}?compose=1`, location.href).href : null
    }

    _tagFromPanel(form) {
        if (form.querySelector("input[name='frame'][value='panel']")) return
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "frame"
        input.value = "panel"
        form.append(input)
    }

    _newConversation() {
        const body = new FormData()
        body.append("purpose", "chat")
        fetch("/assistant/new_conversation", {
            method: "POST",
            headers: { "X-CSRF-Token": this._csrf(), Accept: "application/json" },
            body,
            credentials: "same-origin"
        })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (data?.conversation_id) window.location.replace(`${this.urlValue}?conversation_id=${encodeURIComponent(data.conversation_id)}`)
            })
            .catch(() => {})
    }

    // Any form but the composer (Delete conversation, from the pane's menu)
    // posts in place and comes back to the pane, rather than following its
    // redirect into the full app inside the frame.
    _submit(event) {
        const form = event.target
        // A card's question for Jane (Email: "Draft an email to ...") belongs
        // in the panel's chat. Posted from the card it followed the redirect
        // and drew the whole app inside the card (Pieter, 2026-10-16).
        if (this.fitValue && form instanceof HTMLFormElement && new URL(form.action, location.href).pathname === "/assistant/message") {
            event.preventDefault()
            event.stopImmediatePropagation()
            const data = new FormData(form)
            const text = String(data.get("message") || "").trim()
            if (text) this._post({ type: "ask", text, fresh: data.get("new_conversation") === "1" })
            return
        }
        // A card's forms answer in place (contact_details_response's turbo
        // stream for the panel surface), as they do in the app's sidebar. The
        // ones that end in a redirect come back to this card instead of
        // drawing the app in it (JanePanelFrameReturn), told by this tag.
        if (this.fitValue) {
            if (form instanceof HTMLFormElement) this._tagFromPanel(form)
            return
        }
        if (!(form instanceof HTMLFormElement) || form.matches("form.assistant-compose")) return
        event.preventDefault()
        event.stopImmediatePropagation()
        const confirmText = form.dataset.turboConfirm
        if (confirmText && !window.confirm(confirmText)) return
        fetch(form.action, { method: "POST", body: new FormData(form), credentials: "same-origin", redirect: "manual" })
            .finally(() => window.location.replace(this.urlValue))
    }

    _csrf() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
